import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { UzInput, UzSwitch } from '@utazzo/ui';
import { ChevronDown, GripVertical, Trash2 } from 'lucide-react';
import React from 'react';

import { SectionForm } from '../section-forms';
import { sectionIcon, sectionLabel, summaryFor } from '../section-presets';

import type { PageSection } from '../pages.api';

interface SortableSectionCardProps {
  section: PageSection;
  index: number;
  open: boolean;
  canWrite: boolean;
  onToggleOpen: () => void;
  onSaveTitle: (title: string | null) => void;
  onSaveSubtitle: (subtitle: string | null) => void;
  onChangeData: (next: Record<string, unknown>) => void;
  onToggleVisible: () => void;
  onDelete: () => void;
}

/**
 * One draggable section in the editor's left pane: a sortable accordion row.
 * The grip handle carries the dnd-kit drag listeners; the header toggles the
 * body which renders the per-type <SectionForm>. Reordering is handled by the
 * parent <DndContext>.
 */
export const SortableSectionCard = ({
  section,
  index,
  open,
  canWrite,
  onToggleOpen,
  onSaveTitle,
  onSaveSubtitle,
  onChangeData,
  onToggleVisible,
  onDelete,
}: SortableSectionCardProps): React.JSX.Element => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: section.id,
    disabled: !canWrite,
  });
  const Icon = sectionIcon(section.sectionType);

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`rounded-lg border bg-(--bg) ${
        open ? 'border-(--border-strong) shadow-sm' : 'border-(--border)'
      } ${isDragging ? 'z-10 opacity-60 shadow-md' : ''}`}
    >
      <div className="flex items-center gap-2 px-2.5 py-2.5">
        <button
          type="button"
          className="cursor-grab text-(--fg-faint) hover:text-(--fg) active:cursor-grabbing disabled:cursor-default disabled:opacity-30"
          disabled={!canWrite}
          aria-label="Drag to reorder"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <span className="w-5 text-center text-[10.5px] font-medium text-(--fg-faint) tabular-nums">
          {String(index + 1).padStart(2, '0')}
        </span>
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-(--z-100) text-(--fg-muted)">
          <Icon className="h-3.5 w-3.5" />
        </span>
        <button
          type="button"
          className="flex min-w-0 flex-1 items-center gap-2 text-left"
          onClick={onToggleOpen}
        >
          <span className="shrink-0 text-sm font-medium text-(--fg)">
            {sectionLabel(section.sectionType)}
          </span>
          <span className="truncate text-xs text-(--fg-faint)">
            {section.title || summaryFor(section.sectionType, section.data)}
          </span>
        </button>
        {canWrite && (
          <>
            <UzSwitch
              checked={section.isVisible}
              onCheckedChange={onToggleVisible}
              aria-label="Visible"
            />
            <button
              type="button"
              className="text-(--fg-faint) hover:text-(--ds-error)"
              onClick={onDelete}
              aria-label="Delete section"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        )}
        <button
          type="button"
          onClick={onToggleOpen}
          className="text-(--fg-faint)"
          aria-label={open ? 'Collapse' : 'Expand'}
        >
          <ChevronDown
            className="h-4 w-4 transition-transform"
            style={{ transform: open ? 'rotate(180deg)' : 'none' }}
          />
        </button>
      </div>

      {open && (
        <div className="border-t border-(--border) px-3 py-3">
          <label className="mb-1 block text-xs font-medium text-(--fg-muted)">
            Heading / title
          </label>
          <UzInput
            defaultValue={section.title ?? ''}
            placeholder="Shown as the section heading where applicable"
            disabled={!canWrite}
            onBlur={(e) => onSaveTitle(e.target.value || null)}
          />
          <label className="mt-3 mb-1 block text-xs font-medium text-(--fg-muted)">
            Subheading / description
          </label>
          <UzInput
            defaultValue={section.subtitle ?? ''}
            placeholder="Shown under the heading where applicable"
            disabled={!canWrite}
            onBlur={(e) => onSaveSubtitle(e.target.value || null)}
          />
          <div className="mt-3 border-t border-(--border) pt-3">
            <SectionForm
              sectionType={section.sectionType}
              data={(section.data as Record<string, unknown>) ?? {}}
              onChange={onChangeData}
              disabled={!canWrite}
            />
          </div>
        </div>
      )}
    </div>
  );
};
